import { has } from '@/commonFunctions/utils'
import { More } from '@/components/icons'
import { EVENT_MODIFY, MATCH_SOURCE_DUPR, MATCH_SOURCE_LEAGUE } from '@/constants/Common'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { BracketMatch, Team } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { isEmpty } from 'lodash'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { eventSelector } from '../selectors'

type Props = {
  match: BracketMatch
  sortedTeams: Team[]
  addSubPlayers: () => void
  onForfeit: () => void
  onEditScores: () => void
  onAddScores: () => void
  onDeleteMatch: () => void
  onValidateMatch: () => void
  isPlayerInMatch: boolean
}

export const MatchMenu = ({
  match,
  sortedTeams,
  addSubPlayers,
  onForfeit,
  onEditScores,
  onAddScores,
  onDeleteMatch,
  onValidateMatch,
  isPlayerInMatch,
}: Props) => {
  const { t } = useTranslation()

  const { bracket } = useAppSelector(eventSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const isClubDirector = useMemo(() => has(permissions, EVENT_MODIFY), [permissions])
  const team = 'Viewer'

  const checkSubstitution = () => {
    let noOfPlayers = 0
    let noOfAllowedSub = 0
    sortedTeams?.forEach((match) => {
      if (match?.player1) {
        noOfPlayers += 1
      }
      if (match?.player1.allowSubstitution === false) {
        noOfAllowedSub += 1
      }
      if (match?.player2) {
        noOfPlayers += 1
      }
      if (match?.player2 && match?.player2.allowSubstitution === false) {
        noOfAllowedSub += 1
      }
    })
    if (noOfPlayers === noOfAllowedSub) {
      return true
    }
    return false
  }

  let matchCreatedTeam: any = []
  const teamA = [sortedTeams?.[0]?.player1?.id, sortedTeams[0]?.player2?.id]
  const teamB = [sortedTeams?.[1]?.player1?.id, sortedTeams[1]?.player2?.id]
  if (teamA.includes(match?.userId)) {
    matchCreatedTeam = teamA
  } else if (teamB.includes(match?.userId)) {
    matchCreatedTeam = teamB
  }

  return (
    <Menu as="div" className="relative inline-block text-left w-6 h-6">
      <Menu.Button>
        <More className="w-6 h-6" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute z-50 top-0 right-0 mt-2 w-36 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="flex flex-col gap-y-1 px-1 py-1 ">
            {isClubDirector &&
            match?.leagueMatchStatus === 'NOT_CONFIRMED' &&
            !match?.isForfeited &&
            !isEmpty(match?.teams[1]) ? ( // if director
              <>
                {match?.confirmed ? null : match.matchScoreAdded ? ( // score added? also if match is validated no buttons will be displayed.
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          }`}
                          onClick={onValidateMatch}
                        >
                          {t('event.validate')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          } `}
                          onClick={onEditScores}
                        >
                          {t('event.editScores')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          } `}
                          onClick={onForfeit}
                        >
                          {t('event.forfeit')}
                        </button>
                      )}
                    </Menu.Item>
                  </>
                ) : (
                  !isEmpty(match?.teams[1]) && (
                    <>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                              active && 'bg-slate-300'
                            } `}
                            onClick={onAddScores}
                          >
                            {t('event.addScores')}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                              active && 'bg-slate-300'
                            } `}
                            onClick={onForfeit}
                          >
                            {t('event.forfeit')}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                              checkSubstitution() ? 'bg-slate-300 text-opacity-50' : active && 'bg-slate-300'
                            } `}
                            disabled={checkSubstitution()}
                            onClick={addSubPlayers}
                          >
                            {t('event.substitutes')}
                          </button>
                        )}
                      </Menu.Item>
                    </>
                  )
                )}
              </>
            ) : !match?.isForfeited &&
              isClubDirector &&
              match?.leagueMatchStatus === 'CONFIRMED' &&
              ((!match?.isNextRoundConfirmed && bracket?.status === 'ACTIVE') ||
                bracket?.status === 'COMPLETE' ||
                bracket?.canShowStandings) ? (
              <>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                        active && 'bg-slate-300'
                      } `}
                      onClick={onEditScores}
                    >
                      {t('event.editScores')}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                        active && 'bg-slate-300'
                      } `}
                      onClick={onForfeit}
                    >
                      {t('event.forfeit')}
                    </button>
                  )}
                </Menu.Item>
              </>
            ) : (
              <>
                {team === 'Viewer' || !user?.isValidEmail || match?.matchSource === MATCH_SOURCE_LEAGUE ? (
                  !isEmpty(match?.teams[1]) && isPlayerInMatch && !match.matchScoreAdded ? (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          } `}
                          onClick={onAddScores}
                        >
                          {t('event.addScores')}
                        </button>
                      )}
                    </Menu.Item>
                  ) : match.matchScoreAdded &&
                    (![...teamA, ...teamB].includes(user?.id as number) ||
                      ![...teamA, ...teamB].includes(match?.userId)) ? null : match?.confirmed ||
                    (!teamA.includes(user?.id as number) && !teamB.includes(user?.id as number)) ||
                    matchCreatedTeam.includes(user?.id) ? null : (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          } `}
                          onClick={onValidateMatch}
                        >
                          {t('event.validate')}
                        </button>
                      )}
                    </Menu.Item>
                  )
                ) : match?.confirmed ? null : match?.confirmed &&
                  match?.matchSource === MATCH_SOURCE_DUPR &&
                  sortedTeams[0]?.player1?.id === 0 ? (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                          active && 'bg-slate-300'
                        } `}
                        onClick={onDeleteMatch}
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </Menu.Item>
                ) : (
                  !match?.confirmed &&
                  match?.matchSource === MATCH_SOURCE_DUPR && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`text-navy-900 font-medium w-full text-left rounded px-3 py-2 text-sm ${
                            active && 'bg-slate-300'
                          } `}
                          onClick={onValidateMatch}
                        >
                          {t('event.validate')}
                        </button>
                      )}
                    </Menu.Item>
                  )
                )}
              </>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
